import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Target, Brain, Shield, Lock, UserCheck, Heart, FileCheck, CheckCircle2 } from 'lucide-react';
import { complianceFrameworks } from '../data/mock';

const Compliance = () => {
  const iconMap = {
    Target: Target,
    Brain: Brain,
    Shield: Shield,
    Lock: Lock,
    UserCheck: UserCheck,
    Heart: Heart,
    FileCheck: FileCheck
  };

  const categories = {
    Framework: complianceFrameworks.filter(f => f.category === 'Framework'),
    'AI Governance': complianceFrameworks.filter(f => f.category === 'AI Governance'),
    Regulation: complianceFrameworks.filter(f => f.category === 'Regulation' || f.category === 'Financial Regulation'),
    Architecture: complianceFrameworks.filter(f => f.category === 'Architecture'),
    Compliance: complianceFrameworks.filter(f => f.category === 'Compliance')
  };

  return (
    <div className="page-container">
      {/* Hero Section */}
      <section className="compliance-hero">
        <div className="compliance-hero-content">
          <Badge className="hero-badge">Compliance & Frameworks</Badge>
          <h1 className="compliance-hero-title">
            Comprehensive Compliance Alignment
          </h1>
          <p className="compliance-hero-description">
            Our platforms align with 20+ global compliance frameworks and security standards, ensuring your organization meets regulatory requirements while implementing best-practice security.
          </p>
        </div>
      </section>

      {/* Framework Categories */}
      {Object.entries(categories).map(([category, frameworks], catIndex) => {
        if (frameworks.length === 0) return null;
        
        return (
          <section key={catIndex} className={`section ${catIndex % 2 === 0 ? 'bg-white' : 'bg-light'}`}>
            <div className="section-content">
              <div className="section-header">
                <h2 className="section-title">{category}</h2>
              </div>
              <div className="compliance-grid">
                {frameworks.map((framework) => {
                  const IconComponent = iconMap[framework.icon] || Shield;
                  
                  return (
                    <Card key={framework.id} className="compliance-card">
                      <CardHeader>
                        <div className="compliance-icon">
                          <IconComponent className="icon" />
                        </div>
                        <CardTitle>{framework.name}</CardTitle>
                        <CardDescription>{framework.fullName}</CardDescription>
                      </CardHeader>
                      <CardContent>
                        <p className="compliance-description">{framework.description}</p>
                        <div className="compliance-alignment">
                          <h4 className="alignment-subtitle">IrisTechLabs Alignment</h4>
                          <p className="alignment-text">{framework.alignment}</p>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </div>
          </section>
        );
      })}

      {/* Key Frameworks Deep Dive */}
      <section className="section frameworks-detail">
        <div className="section-content">
          <div className="section-header">
            <h2 className="section-title">Key Framework Implementation</h2>
            <p className="section-description">
              How our platforms implement critical security frameworks
            </p>
          </div>
          
          {/* CTEM Framework */}
          <Card className="framework-detail-card">
            <CardHeader>
              <CardTitle className="framework-title">Gartner CTEM Framework</CardTitle>
              <CardDescription>5-Stage Continuous Threat Exposure Management</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="framework-stages">
                <div className="stage">
                  <div className="stage-number">1</div>
                  <div className="stage-content">
                    <h4 className="stage-title">Scoping</h4>
                    <p className="stage-text">Define attack surface and critical assets</p>
                  </div>
                </div>
                <div className="stage">
                  <div className="stage-number">2</div>
                  <div className="stage-content">
                    <h4 className="stage-title">Discovery</h4>
                    <p className="stage-text">Continuous asset and vulnerability discovery</p>
                  </div>
                </div>
                <div className="stage">
                  <div className="stage-number">3</div>
                  <div className="stage-content">
                    <h4 className="stage-title">Prioritization</h4>
                    <p className="stage-text">AI-powered risk scoring and business impact analysis</p>
                  </div>
                </div>
                <div className="stage">
                  <div className="stage-number">4</div>
                  <div className="stage-content">
                    <h4 className="stage-title">Validation</h4>
                    <p className="stage-text">Automated security control testing with BAS</p>
                  </div>
                </div>
                <div className="stage">
                  <div className="stage-number">5</div>
                  <div className="stage-content">
                    <h4 className="stage-title">Mobilization</h4>
                    <p className="stage-text">Orchestrated remediation and continuous improvement</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Zero Trust Architecture */}
          <Card className="framework-detail-card">
            <CardHeader>
              <CardTitle className="framework-title">Zero Trust Architecture</CardTitle>
              <CardDescription>Never Trust, Always Verify</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="zero-trust-pillars">
                <div className="pillar">
                  <CheckCircle2 className="pillar-icon" />
                  <h4 className="pillar-title">Identity Verification</h4>
                  <p className="pillar-text">Continuous authentication and authorization</p>
                </div>
                <div className="pillar">
                  <CheckCircle2 className="pillar-icon" />
                  <h4 className="pillar-title">Device Security</h4>
                  <p className="pillar-text">Device health and compliance validation</p>
                </div>
                <div className="pillar">
                  <CheckCircle2 className="pillar-icon" />
                  <h4 className="pillar-title">Network Segmentation</h4>
                  <p className="pillar-text">Micro-segmentation and least privilege access</p>
                </div>
                <div className="pillar">
                  <CheckCircle2 className="pillar-icon" />
                  <h4 className="pillar-title">Application Security</h4>
                  <p className="pillar-text">Application-layer access controls</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Regional Compliance */}
      <section className="section regional-compliance">
        <div className="section-content">
          <div className="section-header">
            <h2 className="section-title">Regional Compliance Expertise</h2>
            <p className="section-description">
              Specialized knowledge of regional regulations
            </p>
          </div>
          <div className="regional-grid">
            <Card className="regional-card">
              <CardHeader>
                <CardTitle>India</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="regional-list">
                  <li><CheckCircle2 className="list-icon" /> DPDP (Digital Personal Data Protection)</li>
                  <li><CheckCircle2 className="list-icon" /> RBI Master Direction on Cyber Resilience</li>
                  <li><CheckCircle2 className="list-icon" /> CERT-In Guidelines</li>
                  <li><CheckCircle2 className="list-icon" /> IT Act and Amendments</li>
                </ul>
              </CardContent>
            </Card>
            <Card className="regional-card">
              <CardHeader>
                <CardTitle>European Union</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="regional-list">
                  <li><CheckCircle2 className="list-icon" /> GDPR (General Data Protection Regulation)</li>
                  <li><CheckCircle2 className="list-icon" /> DORA (Digital Operational Resilience Act)</li>
                  <li><CheckCircle2 className="list-icon" /> NIS2 Directive</li>
                  <li><CheckCircle2 className="list-icon" /> ePrivacy Directive</li>
                </ul>
              </CardContent>
            </Card>
            <Card className="regional-card">
              <CardHeader>
                <CardTitle>United States</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="regional-list">
                  <li><CheckCircle2 className="list-icon" /> HIPAA (Healthcare)</li>
                  <li><CheckCircle2 className="list-icon" /> SOC 2 Type II</li>
                  <li><CheckCircle2 className="list-icon" /> NIST Cybersecurity Framework</li>
                  <li><CheckCircle2 className="list-icon" /> CCPA / CPRA (California)</li>
                </ul>
              </CardContent>
            </Card>
            <Card className="regional-card">
              <CardHeader>
                <CardTitle>Global Standards</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="regional-list">
                  <li><CheckCircle2 className="list-icon" /> ISO 27001 / 27002</li>
                  <li><CheckCircle2 className="list-icon" /> PCI-DSS</li>
                  <li><CheckCircle2 className="list-icon" /> IEC 62443 (Industrial)</li>
                  <li><CheckCircle2 className="list-icon" /> CIS Controls</li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="cta-content">
          <h2 className="cta-title">Need Compliance Guidance?</h2>
          <p className="cta-description">
            Talk to our compliance experts about your regulatory requirements
          </p>
          <Button asChild size="lg" className="cta-button-primary">
            <Link to="/contact">Schedule Compliance Consultation</Link>
          </Button>
        </div>
      </section>
    </div>
  );
};

export default Compliance;
